import { useMemo } from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {RefreshCw,} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFeedback } from '@/features/performance';
import {
  TABS,
  type TabId,
} from './performance-constants';
import { FeedbackView } from './feedback-view';
import { ReviewsView } from './reviews-view';
import { GoalsView } from './goals-view';

export function PerformancePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = useMemo<TabId>(() => {
    if (location.pathname.includes('/reviews')) return 'reviews';
    if (location.pathname.includes('/goals')) return 'goals';
    return 'feedback';
  }, [location.pathname]);

  const {
    refetch, isFetching 
  } = useFeedback({});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Performance</h1>
          <p className="text-muted-foreground">Track and manage team performance feedback</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <TabNavigation activeTab={activeTab} onNavigate={navigate} />

      {activeTab === 'reviews' && <ReviewsView />}

      {activeTab === 'goals' && <GoalsView />}

      {activeTab === 'feedback' && <FeedbackView />}
    </div>
  );
}

interface TabNavigationProps {
  readonly activeTab: TabId;
  readonly onNavigate: (href: string) => void;
}

function TabNavigation({
  activeTab,
  onNavigate,
}: TabNavigationProps) {
  return (
    <div className="border-b">
      <nav className="flex gap-4">
        {TABS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onNavigate(tab.href)}
              className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
              }`}
            >
              <TabIcon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
